import { getConsentEnv } from "@/services/consent/env";

import ConsentService, {
  type Consent,
  type ConsentSignatures,
} from "./consent-service";

export const CONSENT_SIGNATURE_HEADER = "x-amz-meta-consent-signature";

interface DefaultConsentServiceOptions {
  /** Consent CDN base URL, e.g. `https://login-registration.ciam-dev.fielmann.com`. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

interface RawConsent {
  id: string;
  required: boolean;
  shortText: string;
}

/** Builds the consent JSON URL from the CDN base URL and locale. */
export function buildConsentUrl(locale: string, baseUrl: string): string {
  const normalisedBase = baseUrl.replace(/\/+$/, "");
  return `${normalisedBase}/consent/consent-${locale}.json`;
}

export default class DefaultConsentService implements ConsentService {
  private readonly baseUrl?: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: DefaultConsentServiceOptions = {}) {
    const env = getConsentEnv();
    this.baseUrl = options.baseUrl ?? env.baseUrl;
    this.fetchImpl =
      options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  }

  async getAllConsents(locale: string): Promise<Consent[]> {
    if (!this.baseUrl) {
      throw new Error("Consent URL is not configured");
    }

    const url = buildConsentUrl(locale, this.baseUrl);

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        headers: {
          Accept: "application/json",
        },
      });
    } catch (networkError) {
      console.error(
        "[consent] fetch threw (network/CORS)",
        { url },
        networkError,
      );
      throw new Error("Could not fetch consents");
    }

    if (!response.ok) {
      console.error("[consent] non-OK response", {
        url,
        status: response.status,
        statusText: response.statusText,
      });
      throw new Error("Could not fetch consents");
    }

    const metaDataSignature = response.headers.get(CONSENT_SIGNATURE_HEADER);

    if (!metaDataSignature) {
      console.error("[consent] signature header missing", { url });
      throw new Error("Signature not present");
    }

    const responseData = (await response.json()) as RawConsent[];
    const signatures = JSON.parse(metaDataSignature) as ConsentSignatures;

    return responseData.map((consent) => {
      const signature = signatures[consent.id];

      if (!signature) {
        console.error("[consent] no signature for consent id", {
          url,
          consentId: consent.id,
          signatureKeys: Object.keys(signatures),
        });
        throw new Error(`Signature not present for consent "${consent.id}"`);
      }

      return {
        ...consent,
        signature,
      };
    });
  }
}
